import React, { useState, useEffect, ReactNode, PropsWithChildren, useCallback } from 'react';
import { useRouter } from 'next/router';
import ExpHeader from 'components/header/exp';
import Footer from 'components/footer/footer';
import { AnalyticsController } from 'analytics/analytics-controller';
import { buildClassName } from 'utils/build-class-name';
import LoadingSpinner from 'components/loading-spinner';
import { useFeaturesContext } from 'contexts/features';
import { AREA_LISTINGS_ROUTE, RouteName, NEWHOMES_ROUTE, Route } from 'components/dynamic-page/route-matchers';
import { useThemeContext, useUser } from 'contexts';
import styles from './search-layout.module.scss';
import { TenantId } from '@tenants/types';
import { SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { useIsIpadMini, useIsMobile } from 'hooks/use-size-class';
import { shouldShowAccessibilityWidget } from 'utils/accessibility';
import storage from 'utils/storage';
import { WebVitals } from 'components/web-vitals';
import { useRouterLoading } from 'hooks/use-router-loading';
import { useIsomorphicLayoutEffect } from 'types/react';
import ResponsiveContainer from 'components/atomic/atoms/responsive-container';
import { SearchHeaderMobile } from 'sections/header/search-header/mobile';
import SearchPrediction from 'data/search-predictions';
import { useSearchResultClick } from 'hooks/use-search-result-click';
import { useUserContext } from 'contexts/user';
import {
  GTM_SEARCH_HEADER_MOBILE_AUTH_BUTTON_CLICK,
  GTM_SEARCH_HEADER_MOBILE_MENU_BUTTON_CLICK,
  GTM_SEARCH_HEADER_MOBILE_SEARCH_BAR_CLICK,
} from 'constants/events';

type SearchLayoutProps = PropsWithChildren<{
  className?: string;
  renderHeader?: () => ReactNode;
  showMobileBottomNav?: boolean;
  routeName?: RouteName;
  isShowing404Page?: boolean;
  showFooter?: boolean;
  noLoadingOnSamePath?: boolean;
}>;

const DefaultRenderHeader = ({ tenantId, routeName, isShowing404Page }: { tenantId: TenantId; routeName?: RouteName; isShowing404Page?: boolean }) => {
  const isMobile = useIsMobile();
  const isIpadMini = useIsIpadMini();
  const shouldShowMobileView = isMobile && !isIpadMini;
  const isSearchOrAreaPage = routeName === SEARCH_PAGE_ROUTE_NAME || routeName === AREA_LISTINGS_ROUTE;
  const showAdvancedSearch = shouldShowMobileView && !isSearchOrAreaPage;
  const [isLocationDropdownActive, setIsLocationDropdownActive] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [searchBarPlaceholder, setSearchBarPlaceholder] = useState<string>();
  const onLocationSearchClick = useSearchResultClick();
  const transparentHeader = (routeName === SEARCH_PAGE_ROUTE_NAME || routeName === AREA_LISTINGS_ROUTE) && !isLocationDropdownActive;
  const { isAuthenticated } = useUserContext();

  const handleLocationSearchClick = useCallback((searchPrediction: SearchPrediction) => {
    setSearchBarPlaceholder(searchPrediction.description.split(',')[0]);
    onLocationSearchClick(searchPrediction);
    setIsLocationDropdownActive(false);
    setLocationQuery(searchPrediction.description);
  }, [onLocationSearchClick]);

  // Analytics handlers
  const handleAuthButtonClick = useCallback(() => {
    AnalyticsController.trackEvent(
      GTM_SEARCH_HEADER_MOBILE_AUTH_BUTTON_CLICK,
      'User Interaction',
      isAuthenticated ? 'authenticated' : 'unauthenticated'
    );
  }, [isAuthenticated]);

  const handleMenuButtonClick = useCallback(() => {
    AnalyticsController.trackEvent(
      GTM_SEARCH_HEADER_MOBILE_MENU_BUTTON_CLICK,
      'Navigation'
    );
  }, []);

  const handleSearchBarClick = useCallback(() => {
    AnalyticsController.trackEvent(
      GTM_SEARCH_HEADER_MOBILE_SEARCH_BAR_CLICK,
      'Search Interaction'
    );
  }, []);

  return (
    <>
      <ResponsiveContainer show="mobile-small" renderMode="conditional">
        <SearchHeaderMobile className={transparentHeader && styles['transparent-header']} tenantId={tenantId} locationQuery={locationQuery} isLocationDropdownActive={isLocationDropdownActive} setLocationQuery={setLocationQuery} setIsLocationDropdownActive={setIsLocationDropdownActive} onLocationClick={handleLocationSearchClick} placeholder={searchBarPlaceholder} onAuthButtonClick={handleAuthButtonClick} onMenuButtonClick={handleMenuButtonClick} onSearchBarClickAnalytics={handleSearchBarClick} />
      </ResponsiveContainer>
      <ResponsiveContainer show="tablet-small-up" renderMode="conditional">
        <ExpHeader tenantId={tenantId} showAdvancedSearch={showAdvancedSearch} isSearchOrAreaPage={isSearchOrAreaPage} isShowing404Page={isShowing404Page} />
      </ResponsiveContainer>
    </>
  );
};

export default function SearchLayout({
  children,
  className = '',
  showFooter = true,
  routeName,
  renderHeader,
  isShowing404Page,
  noLoadingOnSamePath = false,
}: SearchLayoutProps) {
  const { isLoading } = useRouterLoading({ noLoadingOnSamePath });
  const router = useRouter();
  const { tenantId } = useThemeContext(); // FIXME: Hack to get the tenant id :_(
  const { areDelayedScriptsLoaded, features } = useFeaturesContext();
  const { siteLocation } = useUser();

  // Accessibility widget state management (moved from app level)
  const [showAccessibilityWidget, setShowAccessibilityWidget] = useState(shouldShowAccessibilityWidget(tenantId));

  useIsomorphicLayoutEffect(() => {
    const hideAccessibilityWidget = storage.get('accessibilityWidgetClosed');
    // Hide only if value is true
    if (hideAccessibilityWidget) setShowAccessibilityWidget(false);
  }, []);

  useEffect(() => {
    if (areDelayedScriptsLoaded) {
      AnalyticsController?.trackDelayedPageView();
    }

  }, [areDelayedScriptsLoaded]);

  // Footer logic
  const renderFooter = router.route.match(/^((?!\/team\/|\/search).)*$/) && showFooter;
  const footerRoute = router.pathname.includes('/newhomes') ? NEWHOMES_ROUTE : router.route as Route;

  return (
    <>
      <WebVitals />
      {renderHeader ? renderHeader() : <DefaultRenderHeader tenantId={tenantId} routeName={routeName} isShowing404Page={isShowing404Page} />}
      {isLoading && (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      )}
      {!isLoading && (
        <div className={buildClassName(styles.page, className)}>
          {children}
        </div>
      )}
      {renderFooter && (
        <Footer
          canShowAccessibilityWidget={showAccessibilityWidget}
          tenantId={tenantId}
          countryCode={siteLocation}
          route={footerRoute}
          features={features}
        />
      )}
    </>
  );
}

export { SearchLayout };
export type { SearchLayoutProps };
